import { ChevronDown, ChevronUp, Settings, Trash2 } from 'lucide-react';
import type { LeadFormField } from '../../../lib/crm/leadForms';

type Props = {
  fields: LeadFormField[];
  onMoveUp: (fieldId: string) => void;
  onMoveDown: (fieldId: string) => void;
  onOpenSettings: (field: LeadFormField) => void;
  onRemove: (fieldId: string) => void;
};

const shell = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
};

export default function LeadFormFieldCard({
  fields,
  field,
  index,
  onMoveUp,
  onMoveDown,
  onOpenSettings,
  onRemove,
}: Props & { field: LeadFormField; index: number }) {
  return (
    <div
      className="bg-white border border-gray-200/60 rounded-[12px] p-3 sm:p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
      style={shell}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
            {field.label}
            {field.mandatory ? <span className="text-red-500 ml-0.5">*</span> : null}
          </p>
          <p className="text-[12px] text-gray-500 mt-0.5">
            {field.type === 'customer_info'
              ? 'Customer information'
              : field.type === 'checkbox'
                ? 'Checkbox'
                : field.isCustom
                  ? `Custom · ${field.type}`
                  : field.type}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => onMoveUp(field.id)}
            disabled={index === 0}
            className="p-2 rounded-[8px] text-gray-500 active:bg-gray-100 disabled:opacity-30 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Move field up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onMoveDown(field.id)}
            disabled={index >= fields.length - 1}
            className="p-2 rounded-[8px] text-gray-500 active:bg-gray-100 disabled:opacity-30 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Move field down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onOpenSettings(field)}
            className="p-2 rounded-[8px] text-gray-500 active:bg-gray-100 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Field settings"
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(field.id)}
            className="p-2 rounded-[8px] text-red-500 active:bg-red-50 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Remove field"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {field.type === 'customer_info' ? (
        <div className="space-y-2 opacity-70 pointer-events-none">
          <div className="grid grid-cols-2 gap-2">
            <div className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 text-[12px] text-gray-400 flex items-center">
              First name
            </div>
            <div className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 text-[12px] text-gray-400 flex items-center">
              Last name
            </div>
          </div>
          <div className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 text-[12px] text-gray-400 flex items-center">
            Email
          </div>
          <div className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 text-[12px] text-gray-400 flex items-center">
            Phone
          </div>
        </div>
      ) : field.type === 'checkbox' ? (
        <div className="space-y-1.5 opacity-70 pointer-events-none">
          {(field.options || []).map((opt) => (
            <div key={opt.id} className="flex items-center gap-2 text-[13px] text-gray-500">
              <span className="h-3.5 w-3.5 rounded border border-gray-300" />
              {opt.label}
            </div>
          ))}
        </div>
      ) : field.type === 'textarea' ? (
        <div className="h-16 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[12px] text-gray-400 opacity-70">
          {field.placeholder || 'Long text…'}
        </div>
      ) : (
        <div className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 text-[12px] text-gray-400 flex items-center opacity-70">
          {field.placeholder || `Enter ${field.label.toLowerCase()}…`}
        </div>
      )}
    </div>
  );
}
